import { useNavigate, useParams } from "react-router";
import { Copy } from "lucide-react";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { SummaryBreadcrumbs } from "@/routes/processes/[processId]/htz/summaries/_components/summary-breadcrumbs";
import {
  useHtzSummaryShowQuery,
  useHtzSummaryUpdateStateMutation,
} from "@/services/backend/htz/summary/api/summary-api";
import { Summary } from "@/services/backend/htz/summary/types/summary";
import { SummaryDeleteDialog } from "@/services/backend/htz/summary/components/summary-delete-dialog";
import { SummaryPDF } from "@/services/backend/htz/summary/components/summary-pdf";

export function SummaryRoute() {
  const { summaryId } = useParams();

  const {
    data: summary,
    isLoading,
    error,
  } = useHtzSummaryShowQuery({ id: summaryId! });

  const breadcrumbs = summary && <SummaryBreadcrumbs summary={summary} />;

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {summary && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <SummaryPage summary={summary} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function SummaryPage({ summary }: { summary: Summary }) {
  const navigate = useNavigate();

  // The field names list the top-level keys of the fields on each tab, see
  // summary.ReadyForCustomer in the backend.
  const links = [
    {
      to: "data",
      label: t("Bericht"),
      fieldName: [
        "referenceNumber",
        "title",
        "subtitle",
        "date",
        "coverLetter",
      ],
    },
    { to: "items", label: t("Anlagenberichte"), fieldName: ["items"] },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      fieldName: ["postalAddress", "serviceAddress", "representative"],
    },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={summary} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(summary) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZSummaryReport}
                destinationId={summary.id}
                sourceEntityNames={[CpyName.HTZWorkOrder]}
                trigger={
                  <Button variant="outline">
                    <Copy />
                    {t("Aus Arbeitsschein kopieren")}
                  </Button>
                }
                description={t(
                  "Aus dem gewählten Arbeitsschein werden die Kopfdaten übernommen und für jede RLT-Anlage ein Anlagenbericht erstellt oder aktualisiert. Bereits erfasste Werte der betroffenen Anlagenberichte werden überschrieben.",
                )}
                initialParams={{
                  process: { active: true, values: [summary.processId!] },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
                hideCustomerFilter
              />
              <SummaryDeleteDialog
                summaryId={summary.id}
                onDeleted={() =>
                  navigate(`/processes/htz/${summary.processId!}/summaries`)
                }
              />
            </>
          )}
          <DocumentStateDropdown
            document={summary}
            mutation={useHtzSummaryUpdateStateMutation}
          />
          <SummaryPDF summary={summary} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
